import {computed, effect, Injectable, signal} from '@angular/core';
import {IUser} from '../model/user.model';
import {enGB, fr} from "date-fns/locale";

@Injectable({ providedIn: 'root' })
export class SharedDataService {
    /***********************************
     * Global
     **********************************/
    user = signal<IUser | null>(null);
    lang = signal<'nameEn' | 'nameFr'>('nameFr');
    locale = computed(() => {
        switch (this.lang()) {
            case 'nameFr':
                return 'fr-FR';
            case 'nameEn':
                return 'en-GB';
            default:
                return 'en-GB';
        }
    });
    localeFns = computed(() => {
        switch (this.lang()) {
            case 'nameFr':
                return fr;
            case 'nameEn':
                return enGB;
            default:
                return enGB;
        }
    });
    model = signal<any[]>([
        { code: 'DASHBOARD', label: 'dashboard.title', icon: 'pi pi-fw pi-home', routerLink: '/' },
        { code: 'CALENDAR', label: 'calendar.title', icon: 'fa-regular fa-calendar', routerLink: '/calendar' },
        { code: 'PROFILE', label: 'profil.title', icon: 'fa-regular fa-address-card', routerLink: '/profil' },
    ]);
    menuList = signal<any[]>(this.model());
    langOption = signal([
        { code: 'en', nameEn: 'English', nameFr: 'Anglais'},
        { code: 'fr', nameEn: 'French', nameFr: 'Français'},
    ]);
    isAdmin = computed((): boolean => this.currentUserAsRole(['ADMIN']));
    blockLayout = signal(false);
    yesNoStatus = signal([
        { id: true, nameEn: 'Yes', nameFr: 'Oui' },
        { id: false, nameEn: 'No', nameFr: 'Non' }]
    );

    /***********************************
     * Left menu
     **********************************/
    pendingRequest = signal<number>(0)

    constructor() {
        const isBrowser = typeof window !== 'undefined' && typeof localStorage !== 'undefined';

        if (isBrowser && localStorage != undefined) {
            const rawUser = localStorage.getItem('user');
            if (rawUser) {
                try {
                    this.user.set(JSON.parse(rawUser));
                } catch (e) {
                    console.warn('Failed to parse stored user', e);
                }
            }

            const rawLang = localStorage.getItem('lang');
            if (rawLang === 'nameEn' || rawLang === 'nameFr') {
                this.lang.set(rawLang as 'nameEn' | 'nameFr');
            }

            // persist changes to localStorage and translateService usage
            effect(() => {
                const u = this.user();

                const userLang = this.user()?.lang || 'fr';
                const capitalizedLang = userLang.replace(/^./, c => c.toUpperCase());
                this.lang.set(`name${capitalizedLang}` as 'nameEn' | 'nameFr');

                if (u) {
                    localStorage.setItem('user', JSON.stringify(u));
                    localStorage.setItem('lang', this.lang());
                } else {
                    localStorage.removeItem('user');
                    localStorage.removeItem('lang');
                }
            });

            effect(() => {
                localStorage.setItem('lang', this.lang());
            });
        }
    }

    /**
     * Return true or false if the current user have any role in the list of string in param
     * @param roles
     */
    currentUserAsRole(roles: string[]): boolean {
        const userRole = this.user()?.role;
        if (userRole) {
            return roles.includes(userRole);
        }
        return false;
    }
}
